import api from "./api";
import helperService from "./helper";

const patientTaskService = {};

const endpoint = (taskType) => `/tasks/${taskType}/type/patient`;

patientTaskService.create = function ({ patientID, taskType, task }) {
  return api.post(endpoint(taskType), {
    ...task,
    patient: patientID,
  });
};

patientTaskService.get = function ({
  patientID,
  taskType,
  query,
  config = { filterMongoProps: false },
  silent = false,
}) {
  const options = {
    params: {
      ...query,
      ...(patientID ? { patient: patientID } : {}),
    },
    ...(silent ? { getErrorMessage: () => null } : {}),
  };
  return api.get(endpoint(taskType), options).then((res) => ({
    taskType,
    tasks: config.filterMongoProps
      ? helperService.filterMongoProps(res.data.docs)
      : res.data.docs,
    total: res.data.total,
  }));
};

patientTaskService.getById = function ({ taskType, taskID }) {
  return api.get(`${endpoint(taskType)}/${taskID}`).then((res) => res.data);
};

patientTaskService.update = function ({ taskType, task, taskID }) {
  return api
    .put(`${endpoint(taskType)}/${taskID}`, task)
    .then((res) => res.data);
};

patientTaskService.delete = function ({ taskType, taskID }) {
  return api.delete(`${endpoint(taskType)}/${taskID}`).then((res) => res.data);
};

/**
 * @description Tipos de tarea que el paciente ve en su portal "Mis tareas".
 *              Mismo set que empuja la app mobile (menos reports, que va por un
 *              flujo aparte, y healthConversations, que se resuelve por WhatsApp).
 * @type {string[]}
 */
export const PATIENT_TASK_TYPES = [
  "vitals",
  "medications",
  "checkins",
  "exercises",
  "recommendations",
  "appointments",
];

/**
 * @description Tipos completables por el paciente desde el portal (tienen
 *              endpoint `/complete`). `appointments` es solo lectura.
 * @type {string[]}
 */
export const COMPLETABLE_TASK_TYPES = [
  "vitals",
  "medications",
  "checkins",
  "exercises",
  "recommendations",
];

/**
 * @description Trae TODAS las tareas del paciente logueado (una llamada por
 *              tipo, en paralelo). El backend scopea al paciente por `isPatient`
 *              y a la ventana [ahora−72h, +12h], igual que mobile. Cada tarea
 *              queda tagueada con su `type` (plural) para el ruteo de UI.
 * @returns {Promise<Array<Object>>} tareas mergeadas de todos los tipos
 */
patientTaskService.getMyTasks = async function () {
  const results = await Promise.allSettled(
    PATIENT_TASK_TYPES.map((taskType) =>
      patientTaskService.get({ taskType, silent: true })
    )
  );
  const tasks = [];
  results.forEach((r) => {
    if (r.status === "fulfilled" && r.value && Array.isArray(r.value.tasks)) {
      r.value.tasks.forEach((t) =>
        tasks.push({ ...t, type: r.value.taskType })
      );
    }
  });
  return tasks;
};

/**
 * @description Trae las recomendaciones del paciente para la sección
 *              "Recomendaciones" (se agrupan por categoría en la vista).
 * @returns {Promise<Array<Object>>}
 */
patientTaskService.getMyRecommendations = function () {
  return patientTaskService
    .get({ taskType: "recommendations", silent: true })
    .then((res) => (res && res.tasks ? res.tasks : []))
    .catch(() => []);
};

/**
 * @description Devuelve la signed URL (1h) de la media de una recomendación
 *              (el bucket de archivos es privado). Si ya es una URL completa
 *              (ej. YouTube) la devuelve tal cual. Null si no hay media / falla.
 * @param {string} taskID - _id de la RecommendationTask
 * @returns {Promise<string|null>}
 */
patientTaskService.getRecommendationMediaUrl = function (taskID) {
  return api
    .get(`/tasks/recommendations/type/patient/${taskID}/media-url`)
    .then((res) => (res.data && res.data.url) || null)
    .catch(() => null);
};

/**
 * @description Marca una tarea como completada. Shape del body según tipo:
 *              - vitals:    { answer: [{ name, value }, ...] } (paralelo a answerValues)
 *              - checkins:  { answerValues: [{ selected: [answerId, ...] }, ...] }
 *              - medications/exercises/recommendations: sin body.
 * @param {Object} args
 * @param {string} args.taskType - tipo (plural)
 * @param {string} args.taskID - _id de la tarea
 * @param {string} args.patientID - User._id del paciente (para scopear el complete)
 * @param {Object} [args.payload] - body opcional según tipo
 * @returns {Promise<Object>} tarea actualizada
 */
patientTaskService.complete = function ({
  taskType,
  taskID,
  patientID,
  payload,
}) {
  return api
    .put(
      `/tasks/${taskType}/${taskID}/patients/${patientID}/complete`,
      payload || {}
    )
    .then((res) => res.data);
};

export default patientTaskService;
